import Decimal from 'decimal.js';

import { type Money, type Currency, CURRENCY_EXPONENTS, getMinorUnitExponent } from './types.js';

/** Lossless presentation of aggregate amounts, including values beyond JS safe integers. */
export function formatExactMinorUnits(amount: bigint, currency: string, grouped = false): string {
  const exponent = Object.hasOwn(CURRENCY_EXPONENTS, currency) ? CURRENCY_EXPONENTS[currency] : undefined;
  if (exponent === undefined) return `${amount.toString()} minor units`;
  const negative = amount < 0n;
  const digits = (negative ? -amount : amount).toString().padStart(exponent + 1, '0');
  const whole = exponent === 0 ? digits : digits.slice(0, -exponent);
  const fraction = exponent === 0 ? '' : `.${digits.slice(-exponent)}`;
  return `${negative ? '-' : ''}${grouped ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : whole}${fraction}`;
}

/**
 * Format a Money value for display using Decimal.js at the display boundary only.
 * Returns a human-readable string like "100.50" or "1000" (for JPY).
 */
export function formatDisplay(money: Money, locale?: string): string {
  const exponent = getMinorUnitExponent(money.currency);
  const decimal = new Decimal(money.amount.toString()).div(
    new Decimal(10).pow(exponent),
  );

  if (locale !== undefined) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(Number(decimal.toString()));
  }

  return decimal.toFixed(exponent);
}

/**
 * Parse a string of cents/minor units into a bigint.
 * Accepts plain integer strings like "10050" or "-500".
 */
export function parseMoneyCents(value: string): bigint | null {
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return null;
  }
  return BigInt(trimmed);
}

/**
 * Parse a display string (e.g., "100.50") into minor units bigint
 * based on the currency's exponent.
 */
export function parseDisplayToMinorUnits(
  value: string,
  currency: Currency,
): bigint | null {
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }

  const exponent = getMinorUnitExponent(currency);
  const decimal = new Decimal(trimmed).mul(new Decimal(10).pow(exponent));

  if (!decimal.isInteger()) {
    return null;
  }

  return BigInt(decimal.toFixed(0));
}
